import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ImageDataLike } from 'gatsby-plugin-image';
import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isBrowser } from '../../utils';
import { Image } from '../Image/Image';
import { LinkLabel } from '../linkLabel';
import { Spacer } from '../spacer';
import { Typography } from '../typography';

// Interface

export interface TimelineProps {
  items: TimelineItemProps[];
}

export interface TimelineItemProps {
  heading: string;
  subHeading?: string;
  description?: string;
  image?: { image: { localFile: ImageDataLike } };
  button?: {
    url: string;
    label: string;
  };
}

// Styles

const StyledTimeline = styled.div`
  position: relative;
`;

const StyledTimelineItem = styled.li<{ inView: boolean }>`
  ${({ theme: { space, colors, media } }) => css`
    display: flex;
    flex-direction: column;

    @media (min-width: ${media.medium}) {
      flex-direction: row;
    }

    /* &.in-view {
      ${StyledTypography} {
        background: ${colors.brandTwo100};
      }
    } */

    &:first-of-type {
      ${StyledTimelineNumber} {
        ${StyledBeforeLine} {
          border-radius: ${space.xxxSmall} ${space.xxxSmall} 0 0;
        }
      }
    }

    &:last-child {
      ${StyledTimelineNumber} {
        ${StyledAfterLine} {
          padding-bottom: ${space.large};

          @media (min-width: ${media.medium}) {
            padding-bottom: 0;
            border-radius: 0 0 ${space.xxxSmall} ${space.xxxSmall};
          }
        }
      }

      ${StyledTimelineContent} {
        margin-bottom: 0;
      }

      ${StyledTimelineImage} {
        margin-bottom: 0;
      }
    }

    &:nth-of-type(even) {
      ${StyledTimelineImage} {
        @media (min-width: ${media.medium}) {
          order: 1;
          border-radius: 0 ${space.xxSmall} ${space.xxSmall} 0;
        }
      }

      ${StyledTimelineContent} {
        @media (min-width: ${media.medium}) {
          border-radius: ${space.xxSmall} 0 0 ${space.xxSmall};
        }
      }
    }
  `};

  ${({ inView, theme: { colors } }) =>
    inView &&
    css`
      ${StyledTypography} {
        background: ${colors.brandTwo100};
      }
    `}
`;

const StyledBeforeLine = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    background: ${colors.brandTwo50};
    width: ${space.xxSmall};
    flex: 1;
    padding-top: ${space.large};

    @media (min-width: ${media.medium}) {
      padding-top: 0;
    }
  `};
`;

const StyledTypography = styled(Typography)`
  ${({ theme: { space, colors } }) => css`
    background: ${colors.brandTwo50};
    border-radius: 50%;
    width: ${space.xLarge};
    height: ${space.xLarge};
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    z-index: 1;
  `};
`;

const StyledAfterLine = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    background: ${colors.brandTwo50};
    width: ${space.xxSmall};
    flex: 1;
    padding-bottom: ${space.large};

    @media (min-width: ${media.medium}) {
      padding-bottom: ${space.xxLarge};
    }
  `};
`;

const StyledTimelineNumber = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    flex: 0 0 auto;

    @media (min-width: ${media.medium}) {
      margin-right: ${space.xLarge};
    }
  `};
`;

const StyledTimelineImage = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    background: ${colors.neutral100};
    z-index: 1;
    border-radius: ${space.xxSmall} ${space.xxSmall} 0 0;
    min-height: 200px;

    @media (min-width: ${media.medium}) {
      flex: 1 1 calc(${space.xLarge} * 2);
      margin-bottom: ${space.xxLarge};
      border-radius: ${space.xxSmall} 0 0 ${space.xxSmall};
    }
  `};
`;

const StyledTimelineContent = styled(Spacer)`
  ${({ theme: { space, colors, media } }) => css`
    background: ${colors.neutralWhite};
    padding: ${space.medium};
    z-index: 1;
    border-radius: 0 0 ${space.xxSmall} ${space.xxSmall};

    @media (min-width: ${media.medium}) {
      flex: 1 1 0;
      margin-bottom: ${space.xxLarge};
      padding: ${space.xLarge};
      border-radius: 0 ${space.xxSmall} ${space.xxSmall} 0;
    }
  `};
`;

const StyledDefaultLine = styled.div`
  ${({ theme: { colors } }) => css`
    position: absolute;
    left: 28px;
    background: ${colors.brandTwo50};
    width: 0;
    height: 100%;
    top: 0;
  `};
`;

const StyledDrawLine = styled.div`
  ${({ theme: { colors, media, space } }) => css`
    position: absolute;
    left: calc(50% - 4px);
    background: ${colors.brandTwo100};
    width: 8px;
    top: 0;
    border-radius: ${space.xxxSmall} ${space.xxxSmall} ${space.xxxSmall}
      ${space.xxxSmall};
    /* transition: height 0.1s ease-out; */

    @media (min-width: ${media.medium}) {
      left: 28px;
    }
  `};
`;

// JSX

export const Timeline: FC<TimelineProps> = ({ items, ...rest }) => {
  const [screenWidth, setScreenWidth] = useState(
    isBrowser ? window.innerWidth : undefined
  );
  const [inViewItems, setInViewItems] = useState(
    Array(items?.length ?? 0).fill(false)
  );

  const itemsRef = useRef<HTMLUListElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const greyLineRef = useRef<HTMLDivElement>(null);
  const lineToDrawRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isBrowser) {
      const handleResize = () => {
        setScreenWidth(window.innerWidth);
      };

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  useLayoutEffect(() => {
    if (isBrowser) {
      const handleScroll = () => {
        const timelineItems = itemsRef?.current
          ?.children as unknown as HTMLLIElement[];
        const timeline = timelineRef.current;
        const greyLine = greyLineRef.current;
        const lineToDraw = lineToDrawRef.current;

        const greyLineHeight = greyLine?.offsetHeight;
        const windowDistance = window.scrollY;
        const windowHeight = window.innerHeight / 2;
        const timelineDistance = timeline
          ? timeline.getBoundingClientRect().top + window.scrollY
          : 0;

        if (windowDistance >= timelineDistance - windowHeight) {
          const line = windowDistance - timelineDistance + windowHeight;

          if (lineToDraw && greyLineHeight && line <= greyLineHeight) {
            lineToDraw.style.height = `${line}px`;
          }
        }

        const bottom = lineToDraw
          ? lineToDraw.getBoundingClientRect().bottom + window.scrollY
          : 0;

        const newInViewItems = inViewItems.slice();

        if (timelineItems?.length) {
          Array.from(timelineItems).forEach((item, index) => {
            const itemHeight = item?.offsetHeight;

            const circlePosition =
              item.getBoundingClientRect().top +
              window.scrollY +
              itemHeight / 2 -
              (screenWidth && screenWidth < 768 ? 265 : 80);

            if (bottom > circlePosition) {
              newInViewItems[index] = true;
            } else {
              newInViewItems[index] = false;
            }
          });
        }

        setInViewItems(newInViewItems);
      };

      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [inViewItems, screenWidth]);

  if (!items || items.length <= 0) {
    return null;
  }
  return (
    <StyledTimeline ref={timelineRef} {...rest}>
      <ul ref={itemsRef}>
        {items.map(
          ({ heading, subheading, description, image, button }, index) => (
            <StyledTimelineItem key={index} inView={inViewItems[index]}>
              <StyledTimelineNumber>
                <StyledBeforeLine />
                <StyledTypography
                  variant="headingMedium"
                  color="neutralWhite"
                  component="span"
                >
                  {index + 1}
                </StyledTypography>
                <StyledAfterLine />
              </StyledTimelineNumber>
              {image && (
                <StyledTimelineImage>
                  <Image src={image.image.localFile} alt={heading} />
                </StyledTimelineImage>
              )}

              <StyledTimelineContent spacing="medium">
                <Spacer spacing="xSmall">
                  {heading && (
                    <Typography variant="headingMedium" component="h3">
                      {heading}
                    </Typography>
                  )}
                  {subheading && (
                    <Typography variant="bodyLarge" component="p">
                      {subheading}
                    </Typography>
                  )}
                  {description && (
                    <Typography variant="bodyMedium" component="p">
                      {description}
                    </Typography>
                  )}
                </Spacer>
                {button && <LinkLabel arrowRight {...button} />}
              </StyledTimelineContent>
            </StyledTimelineItem>
          )
        )}
      </ul>
      <StyledDefaultLine className="default-line" ref={greyLineRef} />
      <StyledDrawLine className="draw-line" ref={lineToDrawRef} />
    </StyledTimeline>
  );
};

export default Timeline;
